/*
  Collection and search results: filters, sort, pagination and the intro "Read more".
  Loaded as a module by sections/main-collection.liquid and sections/main-search.liquid.

  <tat-facets data-section-id="..."> wraps the filter sidebar, the results and the mobile drawer.
  - Every change re-renders the section through the Section Rendering API (TAT.fetchSection) and
    updates the URL with history.pushState. Back and forward buttons re-render from the URL.
  - Without JavaScript everything still works: the filter form is a GET form and the pills,
    pagination and sort use plain links and a submit button.
  - The filter form sits in the sidebar from the lg breakpoint and moves into the drawer below it.
*/
const TAT = window.TAT || {};
const DESKTOP = window.matchMedia('(min-width: 64rem)');

const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

class TatFacets extends HTMLElement {
  connectedCallback() {
    // Window-level listeners are attached on every connect and removed on disconnect, so a section
    // re-rendered by the theme editor doesn't leave the old element listening.
    this.onDesktopChange = this.onDesktopChange || (() => this.placeForm());
    this.onPopState = this.onPopState || (() => {
      const url = window.location.pathname + window.location.search;
      if (url !== this.currentUrl) this.apply(window.location.href, { push: false });
    });
    this.currentUrl = window.location.pathname + window.location.search;
    DESKTOP.addEventListener('change', this.onDesktopChange);
    window.addEventListener('popstate', this.onPopState);

    if (this.ready) return;
    this.ready = true;
    this.sectionId = this.dataset.sectionId;
    this.form = this.querySelector('[data-facet-form]');
    this.sidebar = this.querySelector('[data-facets-sidebar]');
    this.drawer = this.querySelector('tat-drawer');
    this.drawerSlot = this.drawer?.querySelector('[data-drawer-slot]');
    this.status = this.querySelector('[data-facets-status]');
    this.requestId = 0;

    this.placeForm();
    this.expandedLists = new Set();
    this.initLists();
    this.applyDensity();

    this.applyPrice = debounce((el) => this.apply(this.formUrl(), { focusId: el.id }), 700);

    this.addEventListener('change', (e) => {
      const el = e.target;
      if (!this.form || el.form !== this.form) return;
      if (el.matches('[data-price-input]')) return this.applyPrice(el);
      this.apply(this.formUrl(), { focusId: el.id });
    });
    this.addEventListener('input', (e) => {
      if (e.target.matches?.('[data-price-input]') && e.target.form === this.form) this.applyPrice(e.target);
    });
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.apply(this.formUrl());
    });
    this.addEventListener('input', (e) => {
      if (e.target.matches?.('[data-facet-search]')) this.searchList(e.target);
    });
    this.addEventListener('click', (e) => {
      const more = e.target.closest('[data-facet-more]');
      if (more) {
        const list = more.closest('[data-facet-list]');
        const group = more.closest('[data-filter-group]')?.dataset.filterGroup;
        const expanded = list.dataset.expanded !== 'true';
        list.dataset.expanded = String(expanded);
        if (group) expanded ? this.expandedLists.add(group) : this.expandedLists.delete(group);
        this.refreshList(list);
        return;
      }
      const preset = e.target.closest('[data-price-preset]');
      if (preset) {
        const inputs = preset.closest('fieldset')?.querySelectorAll('[data-price-input]') || [];
        if (inputs.length === 2) {
          const alreadyOn = preset.getAttribute('aria-pressed') === 'true';
          inputs[0].value = alreadyOn ? '' : preset.dataset.min || '';
          inputs[1].value = alreadyOn ? '' : preset.dataset.max || '';
          this.apply(this.formUrl());
        }
        return;
      }
      const density = e.target.closest('[data-density]');
      if (density) {
        try {
          localStorage.setItem('tat-grid-cols', density.dataset.density);
        } catch (err) {
          /* storage unavailable: the choice lasts for this page only */
          this.densityChoice = density.dataset.density;
        }
        this.applyDensity();
        return;
      }
      const loadMore = e.target.closest('a[data-load-more]');
      if (loadMore && !(e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0)) {
        e.preventDefault();
        this.loadMore(loadMore);
        return;
      }
    });
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-facet-link]');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
      e.preventDefault();
      const isPage = !!link.closest('nav[aria-label]') && !link.closest('[data-drawer-footer]');
      this.apply(link.href, { scroll: isPage });
    });
  }

  disconnectedCallback() {
    DESKTOP.removeEventListener('change', this.onDesktopChange);
    window.removeEventListener('popstate', this.onPopState);
  }

  // Sidebar on desktop, drawer on mobile. Close the drawer when crossing to desktop.
  placeForm() {
    if (!this.form) return;
    const target = DESKTOP.matches || !this.drawerSlot ? this.sidebar : this.drawerSlot;
    if (target && this.form.parentElement !== target) target.appendChild(this.form);
    if (DESKTOP.matches && this.drawer?.classList.contains('is-open')) this.drawer.close();
  }

  formUrl() {
    const action = new URL(this.form.getAttribute('action') || window.location.pathname, window.location.origin);
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(this.form)) {
      if (String(value).trim() !== '') params.append(key, value);
    }
    const query = params.toString();
    return `${action.pathname}${query ? `?${query}` : ''}`;
  }

  async apply(url, { push = true, focusId = null, scroll = false } = {}) {
    const target = new URL(url, window.location.origin);
    const id = ++this.requestId;
    const results = this.querySelector('[data-results]');
    results?.setAttribute('aria-busy', 'true');

    let html;
    try {
      html = await TAT.fetchSection(target.pathname + target.search, this.sectionId);
    } catch (err) {
      window.location.href = target.href;
      return;
    }
    if (id !== this.requestId) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('tat-facets');
    if (!next) {
      window.location.href = target.href;
      return;
    }

    this.swap(next, doc);
    this.initLists();
    this.applyDensity();
    this.currentUrl = target.pathname + target.search;
    if (push) history.pushState({ facets: true }, '', this.currentUrl);

    const count = this.querySelector('[data-results-count]');
    if (this.status) this.status.textContent = count ? count.textContent.trim() : '';

    if (focusId) {
      document.getElementById(focusId)?.focus({ preventScroll: true });
    } else if (count && !this.drawer?.classList.contains('is-open')) {
      count.focus({ preventScroll: true });
    }
    // Let other features (compare checkboxes, reviews stars) refresh the new cards
    document.dispatchEvent(new CustomEvent('facets:updated', { detail: { sectionId: this.sectionId, url: target.href } }));

    if (scroll) {
      const top = this.querySelector('[data-results]');
      if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ block: 'start' });
    }
  }

  // Long filter lists (e.g. Brand): first 8 values, "Show all", and a search box.
  initLists() {
    this.querySelectorAll('[data-facet-list]').forEach((list) => {
      const group = list.closest('[data-filter-group]')?.dataset.filterGroup;
      if (group && this.expandedLists?.has(group)) list.dataset.expanded = 'true';
      this.refreshList(list);
    });
  }

  refreshList(list) {
    const query = (list.querySelector('[data-facet-search]')?.value || '').trim().toLowerCase();
    const expanded = list.dataset.expanded === 'true';
    let shown = 0;
    list.querySelectorAll('[data-facet-value]').forEach((li) => {
      const match = query ? li.dataset.facetValue.includes(query) : true;
      const show = query ? match : expanded || !li.hasAttribute('data-facet-extra');
      li.hidden = !show;
      if (show) shown += 1;
    });
    const more = list.querySelector('[data-facet-more]');
    if (more) {
      more.hidden = !!query;
      more.setAttribute('aria-expanded', String(expanded));
      if (!more.dataset.label) more.dataset.label = more.textContent.trim();
      more.textContent = expanded ? 'Show fewer' : more.dataset.label;
    }
    const none = list.querySelector('[data-facet-none]');
    if (none) none.hidden = shown > 0;
  }

  searchList(input) {
    const list = input.closest('[data-facet-list]');
    if (list) this.refreshList(list);
  }

  // 3 or 4 products per row from xl. Stored per browser; defaults to the layout's own columns.
  applyDensity() {
    let choice = this.densityChoice || null;
    try {
      choice = localStorage.getItem('tat-grid-cols') || choice;
    } catch (err) {
      /* storage unavailable */
    }
    const grid = this.querySelector('[data-product-grid]');
    if (grid) {
      if (choice) grid.dataset.cols = choice;
      else delete grid.dataset.cols;
    }
    const effective = choice || (window.matchMedia('(min-width: 96rem)').matches ? '4' : '3');
    this.querySelectorAll('[data-density]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.density === effective)));
  }

  // Append the next page of products in place. Numbered pagination stays in the page for crawlers.
  async loadMore(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    const label = button.textContent;
    button.textContent = 'Loading...';
    let html;
    try {
      const url = new URL(button.href, window.location.origin);
      html = await TAT.fetchSection(url.pathname + url.search, this.sectionId);
    } catch (err) {
      window.location.href = button.href;
      return;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = this.querySelector('[data-product-grid]');
    const nextItems = doc.querySelectorAll('[data-product-grid] > li');
    let firstNew = null;
    nextItems.forEach((li) => {
      const node = document.importNode(li, true);
      if (!firstNew) firstNew = node;
      grid?.appendChild(node);
    });
    const wrap = this.querySelector('[data-load-more-wrap]');
    const nextWrap = doc.querySelector('[data-load-more-wrap]');
    if (wrap && nextWrap) wrap.replaceWith(document.importNode(nextWrap, true));
    else if (wrap) wrap.remove();
    const pager = this.querySelector('[data-results] nav[aria-label]');
    const nextPager = doc.querySelector('[data-results] nav[aria-label]');
    if (pager && nextPager) pager.replaceWith(document.importNode(nextPager, true));
    firstNew?.querySelector('a[href]:not([tabindex="-1"])')?.focus({ preventScroll: true });
    if (this.status) this.status.textContent = `${nextItems.length} more products loaded`;
    document.dispatchEvent(new CustomEvent('facets:updated', { detail: { sectionId: this.sectionId, url: button.href } }));
    if (!nextWrap) button.textContent = label;
  }

  swap(next, doc) {
    // Filter form: replace the fields, keep the element (it may live in the drawer) and open groups
    const nextForm = next.querySelector('[data-facet-form]');
    if (this.form && nextForm) {
      const open = new Map();
      this.form.querySelectorAll('details[data-filter-group]').forEach((d) => open.set(d.dataset.filterGroup, d.open));
      this.form.innerHTML = nextForm.innerHTML;
      this.form.querySelectorAll('details[data-filter-group]').forEach((d) => {
        if (open.has(d.dataset.filterGroup)) d.open = open.get(d.dataset.filterGroup);
      });
    }

    const results = this.querySelector('[data-results]');
    const nextResults = next.querySelector('[data-results]');
    if (results && nextResults) results.replaceWith(nextResults);

    // Desktop panel heading ("Clear all") and footer count
    ['[data-panel-head]', '[data-panel-foot]'].forEach((sel) => {
      const el = this.querySelector(sel);
      const nextEl = next.querySelector(sel);
      if (el && nextEl) el.innerHTML = nextEl.innerHTML;
    });

    const footer = this.querySelector('[data-drawer-footer]');
    const nextFooter = next.querySelector('[data-drawer-footer]');
    if (footer && nextFooter) footer.innerHTML = nextFooter.innerHTML;

    const summary = document.querySelector('[data-live-summary]');
    const nextSummary = doc.querySelector('[data-live-summary]');
    if (summary && nextSummary) summary.innerHTML = nextSummary.innerHTML;
  }
}
if (!customElements.get('tat-facets')) customElements.define('tat-facets', TatFacets);

